import {Component, OnDestroy} from "@angular/core";
import {FormBuilder, FormGroup} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {Subscription} from "rxjs/internal/Subscription";
import {LocalStorage} from "src/app/services/local-storage";
import {ModalPaymentService} from "src/app/services/ux/modal-payment.service";
import {toastrService} from "src/app/services/ux/toastr.service";
import {WalletService} from "src/app/services/wallet.service";
import {Dogana} from "src/app/models/SearchService/dogana.model";
import {DoganaHttpClientService} from "src/app/data/dogana-http-client.service";
import {HttpErrorResponse} from "@angular/common/http";
import {finalize} from "rxjs/operators";
import {TransactionStatusEmitter} from "src/app/models/Payment/Emitters/transaction-status-emitter";
import { TranslateService } from "@ngx-translate/core";

@Component({
    templateUrl: "dogana-search.component.html"
})
export class DoganaSearchComponent implements OnDestroy {
    // #region Subscriptions
    subscription: Subscription;
    ccSubscription: Subscription;
    finishTransactionSubscription: Subscription;
    // #endregion

    searchForm: FormGroup;

    isLoading = false;

    hasResults = false;
    private _doganaRecord = {} as Dogana;
    public get doganaRecord(): Dogana {
        return this._doganaRecord;
    }

    public set doganaRecord(value: Dogana) {
        this._doganaRecord = value;
        this.hasResults = !!Object.keys(value).length;
    }

    hasParam = false;

    constructor(
        private fb: FormBuilder,
        private doganaDataService: DoganaHttpClientService,
        private toastr: toastrService,
        private walletService: WalletService,
        private route: ActivatedRoute,
        private modalService: ModalPaymentService,
        private ls: LocalStorage,
        private translateService: TranslateService
    ) {
        this.searchForm = this.fb.group({
            barcode: []
        });

        this.subscription = walletService.$walletReadyEmitter.subscribe(
            (isReady: boolean) => {
                if (isReady) {
                    this.processDoganaTransaction();
                }
            }
        );

        this.ccSubscription = walletService.$creditCardReadyEmitter.subscribe(
            (ccSelected: boolean) => {
                if (ccSelected) {
                    this.processDoganaTransaction(ccSelected);
                }
            }
        );

        this.finishTransactionSubscription = this.walletService.$transactionFinish.subscribe(
            (value) => {
                if (value) {
                    this.removeFinishedTransactionFromRecords();
                }
            }
        );

        this.route.params.subscribe((params: any) => {
            if (params.barcode) {
                this.searchForm.controls["barcode"].setValue(params.barcode);

                this.hasParam = true;
                this.onSearch();
            }
        });
    }

    onSearch() {
        if (!this.searchForm.value.barcode) {
            return this.toastr.warning(this.translateService.instant("Services.CompleteBarcode"));
        }

        this.isLoading = true;

        this.doganaDataService
            .Search(this.searchForm.value.barcode)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: (response: Dogana) => {
                    this.doganaRecord = response;
                },
                error: (error: HttpErrorResponse) => {
                    this.doganaRecord = {} as Dogana;
                    switch (error.error) {
                        case "dogana_search_failed":
                            this.toastr.warning(this.translateService.instant("Services.NoInformationFound"));
                            break;
                        case "no_invoices":
                            this.toastr.warning(this.translateService.instant("Services.PackageIsPaid"));
                            break;
                        default:
                            this.toastr.warning(this.translateService.instant("Services.ServerProblem"));
                            break;
                    }
                }
            });
    }

    onPay() {
        const selectedInvoice = this.doganaRecord;

        this.walletService.selectInvoice(selectedInvoice);
        this.walletService.amountOfTransaction = selectedInvoice.retentionPrice + selectedInvoice.commision;

        this.modalService.open();
    }

    processDoganaTransaction(creditCardMethod: boolean = false) {
        const doganaInvoice = {
            invoice: this.walletService.getInvoice(),
            serviceFields: [
                {FieldName: "barcode", FieldValue: this.searchForm.value.barcode}
            ],
            ipAddress: "127.0.0.1",
            deviceId: "EternaWebPosta"
        };

        if (creditCardMethod) {
            this.doganaDataService
                .PayCC(doganaInvoice)
                .pipe(
                    finalize(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe({
                    next: (response) => {
                        if (!response.data) {
                            this.walletService.emitTransactionProgress({
                                    isSuccess: false,
                                    error: response.error.message
                                }
                            );
                            return;
                        }
                        this.walletService.emitTransactionProgress(
                            {
                                isSuccess: true,
                                error: null
                            }
                        );
                        this.ls.set("sn", "dogana");

                        window.location = (response.data.redirect);
                    },
                    error: (error: HttpErrorResponse) => {
                        const emmiterObject: TransactionStatusEmitter = {
                            isSuccess: false,
                            error: error.message
                        };

                        this.walletService.emitTransactionProgress(emmiterObject);
                    }
                });
        } else {
            this.doganaDataService
                .PayWallet(doganaInvoice)
                .pipe(
                    finalize(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe({
                    next: () => {
                        const emmiterObject = {
                            isSuccess: true,
                            error: null
                        };

                        this.walletService.emitTransactionProgress(emmiterObject);
                    },
                    error: (error: HttpErrorResponse) => {
                        const emmiterObject = {
                            isSuccess: false,
                            error: error.error.message
                        };
                        this.walletService.emitTransactionProgress(emmiterObject);
                    },
                    complete: () => {
                        this.isLoading = false;
                    }
                });
        }
    }

    private removeFinishedTransactionFromRecords() {
        this.doganaRecord = {} as Dogana;
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.ccSubscription.unsubscribe();
        this.finishTransactionSubscription.unsubscribe();
    }
}
